import { Component, computed, inject, Injectable, OnDestroy, OnInit, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import {
    FilterChip,
    Page,
    PageResult,
    QuerySortDirection,
    ReactiveSearchHomeComponent,
    SearchService,
    ServiceHandler,
} from '@nucleus/ui-base';
import { Runner } from '../../api/runner-service';
import { RunnerControllerService } from '../../api/runner-service/api/runnerController.service';
import { UiMessageBus } from '../../services/ui-message-broker/ui-message-bus';

interface RunnerDeletedPayload { runnerId: string; }
type RunnerStatus = 'ALL' | 'ONLINE' | 'STALE';

/** Boundary between live and stale runners. 3× the runner-library heartbeat
 *  interval so a single missed heartbeat doesn't flap the UI. */
const STALE_AFTER_MS = 90_000;

/**
 * Filter envelope for the runners list. {@code value} is the omnibar
 * free-text (matches runnerId / name / host / domain / capabilities /
 * connectors); {@code status} narrows to ALL / ONLINE / STALE based on
 * the rolling {@link STALE_AFTER_MS} threshold.
 */
export class RunnerFilter {
    constructor(
        public value: string = '',
        public status: RunnerStatus = 'ALL',
    ) {}
}

/**
 * Client-side adapter that lets {@link RunnersAdminComponent} extend
 * {@link ReactiveSearchHomeComponent}. The runner-service exposes only
 * {@code GET /runners} (no server-side filter endpoint) so the adapter
 * fetches the full list per refresh and applies the filter + sort +
 * pagination locally. Staleness is evaluated against {@code Date.now()}
 * at each filter call.
 */
@Injectable({ providedIn: 'root' })
export class RunnerSearchAdapter implements SearchService<Runner, RunnerFilter> {

    private readonly api: RunnerControllerService = inject(RunnerControllerService);

    search(handler: ServiceHandler<Runner[]>): void {
        handler.onStart?.();
        this.api.findAll1().subscribe({
            next:     rows => handler.onSuccess(rows ?? []),
            error:    err  => handler.onError?.(err),
            complete: ()   => handler.onComplete?.(),
        });
    }

    filter(handler: ServiceHandler<PageResult<Runner>>, filter: RunnerFilter, page?: Page): void {
        handler.onStart?.();
        this.api.findAll1().subscribe({
            next: rows => {
                const cutoff    = Date.now() - STALE_AFTER_MS;
                const sortField = page?.sortField ?? 'lastSeen';
                const sortAsc   = (page?.sortDirection ?? QuerySortDirection.DESC) === QuerySortDirection.ASC;
                const matched   = (rows ?? [])
                    .filter(r => this.matches(r, filter, cutoff))
                    .sort((a, b) => this.compare(a, b, sortField, sortAsc));
                const total = matched.length;
                const size  = page?.pageSize ?? (total || 1);
                const start = (page?.pageNumber ?? 0) * size;
                const slice = page ? matched.slice(start, start + size) : matched;
                handler.onSuccess(new PageResult<Runner>(slice, total, page?.pageNumber ?? 0));
            },
            error:    err => handler.onError?.(err),
            complete: ()  => handler.onComplete?.(),
        });
    }

    private matches(r: Runner, f: RunnerFilter, cutoff: number): boolean {
        if (f.status !== 'ALL') {
            const seen = r.lastSeen ? new Date(r.lastSeen).getTime() : 0;
            if (f.status === 'ONLINE' && seen <  cutoff) return false;
            if (f.status === 'STALE'  && seen >= cutoff) return false;
        }
        const search = (f.value ?? '').trim().toLowerCase();
        if (!search) return true;
        const haystack = [
            r.runnerId, r.name, r.host, r.domain,
            Array.isArray(r.tags) ? r.tags.join(' ') : r.tags,
            Array.isArray(r.connectors)   ? r.connectors.join(' ')   : r.connectors,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(search);
    }

    private compare(a: Runner, b: Runner, field: string, asc: boolean): number {
        const av = (a as any)[field] ?? '';
        const bv = (b as any)[field] ?? '';
        const cmp = String(av).localeCompare(String(bv));
        return asc ? cmp : -cmp;
    }
}

/**
 * Admin grid over the runners table populated by tasksense-runner-service
 * (RunnerRegistryConsumer). Read-only — runners self-register over Kafka,
 * this page just surfaces them.
 *
 * <p>Live updates: subscribes to {@code RUNNER-UPDATED} + {@code RUNNER-DELETED}
 * on the {@link UiMessageBus}. On any event, fires {@code refreshData(true)}
 * so the lib's dataSource reflects current state. A 60s safety poll catches
 * any UiMessage drops.
 *
 * <p>Staleness: a runner is "online" if {@code lastSeen} is within
 * {@link STALE_AFTER_MS} of now. The status filter applies the threshold
 * at filter time; the per-row pill re-evaluates whenever {@code nowTick}
 * advances (every 10s) so a row visibly flips Online → Stale even between
 * refreshes.
 *
 * <p>Migrated to {@code <lib-list-page>} + {@link ReactiveSearchHomeComponent}
 * for chrome consistency. Backend has no server-side filter; the inline
 * {@link RunnerSearchAdapter} applies filter + sort + page locally.
 */
@Component({
    selector: 'app-runners-admin',
    templateUrl: './runners.component.html',
    styleUrl: './runners.component.css',
    standalone: false,
})
export class RunnersAdminComponent
    extends ReactiveSearchHomeComponent<Runner, RunnerFilter>
    implements OnInit, OnDestroy {

    private readonly bus: UiMessageBus = inject(UiMessageBus);

    displayedColumns = ['status', 'name', 'endpoint', 'domain', 'capabilities',
                        'connectors', 'maxConcurrency', 'runnerVersion',
                        'lastSeen', 'registeredAt'];

    /** Wall-clock signal that advances every 10s so the per-row Online/Stale
     *  pill flips on time without a manual refresh. Independent of the SSE
     *  feed: even when no events arrive, the threshold check uses fresh now. */
    readonly nowTick = signal<number>(Date.now());

    private tickHandle:  ReturnType<typeof setInterval> | null = null;
    private pollHandle:  ReturnType<typeof setInterval> | null = null;
    private busSubs:     Subscription[] = [];

    constructor() {
        super(inject(RunnerSearchAdapter), 'lastSeen', QuerySortDirection.DESC);
    }

    // ── ReactiveSearchHomeComponent overrides ───────────────────────────────

    override createFilter(): RunnerFilter {
        return new RunnerFilter();
    }

    override onResetFilter(): void {
        this.filter.update(f => Object.assign(f, new RunnerFilter()));
    }

    override isReadyForSearch(): boolean {
        return this.permissionChecker.hasAccessToViewRunnersAdmin();
    }

    ngOnInit(): void {
        if (!this.permissionChecker.hasAccessToViewRunnersAdmin()) {
            this.showAccessDeny();
            return;
        }

        // Stale-flag tick — only updates the per-row pill display; doesn't
        // refire refreshData (would spam HTTP). Filter staleness re-evaluates
        // on the next refresh (manual / SSE event / 60s poll).
        this.tickHandle = setInterval(() => this.nowTick.set(Date.now()), 10_000);

        // SSE live updates. Server publishes RUNNER-UPDATED on registration /
        // heartbeat and RUNNER-DELETED on cleanup. Each fires a re-fetch so
        // the lib dataSource reflects current state.
        this.busSubs.push(
            this.bus.on<Runner>('RUNNER-UPDATED').subscribe(() => this.refreshData(true))
        );
        this.busSubs.push(
            this.bus.on<RunnerDeletedPayload>('RUNNER-DELETED').subscribe(() => this.refreshData(true))
        );

        // 60s safety poll catches any UiMessage drops.
        this.pollHandle = setInterval(() => this.refreshData(false), 60_000);
    }

    ngOnDestroy(): void {
        if (this.tickHandle) clearInterval(this.tickHandle);
        if (this.pollHandle) clearInterval(this.pollHandle);
        this.busSubs.forEach(s => s.unsubscribe());
    }

    // ── filter wiring ────────────────────────────────────────────────────────

    setStatusFilter(value: RunnerStatus): void {
        this.filter.update(f =>
            Object.assign(Object.create(Object.getPrototypeOf(f)), f, { status: value }));
    }

    activeFilterChips = computed<FilterChip[]>(() => {
        const f = this.filter();
        const chips: FilterChip[] = [];
        if (f.status === 'ONLINE') chips.push({ key: 'status', icon: 'check_circle', label: 'Online', removable: true });
        if (f.status === 'STALE')  chips.push({ key: 'status', icon: 'cloud_off',     label: 'Stale',  removable: true });
        return chips;
    });

    hasRemovableChips(): boolean {
        return this.activeFilterChips().some(c => c.removable);
    }

    removeFilterChip(key: string): void {
        if (key === 'status') this.setStatusFilter('ALL');
    }

    // ── row + cell helpers (preserved) ──────────────────────────────────────

    isStale(runner: Runner): boolean {
        const seen = runner.lastSeen ? new Date(runner.lastSeen).getTime() : 0;
        return seen < this.nowTick() - STALE_AFTER_MS;
    }

    onRowClick(runner: Runner): void {
        if (runner.runnerId) this.navigateTo('/administration/runner', runner.runnerId);
    }

    /** Tooltip for the container pill — friendly name + image when both set. */
    containerTooltip(r: Runner): string {
        const parts: string[] = [];
        if (r.containerName)  parts.push(r.containerName);
        if (r.containerImage) parts.push(r.containerImage);
        return parts.length ? parts.join('\n') : 'Docker container';
    }

    /** Render JSON arrays as a comma-joined string for table display. */
    formatList(value: any): string {
        if (!value) return '';
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
    }
}
